import { FREE_PLAY_LIMIT } from '@archetelos/lib/config';
import { queryOptions } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';

// Freemium VOD gate seam (PLAN §5, Increment 4). Talks to the two SECURITY
// DEFINER RPCs (free_play_status / consume_free_play). The RPCs are the
// authoritative gate; this client layer reads status for display/routing and
// exposes the consume entry the player unit will call on playback start.

// Re-exported so existing app imports keep resolving; the number itself now
// lives in @archetelos/lib/config, shared with the marketing video gate card.
export { FREE_PLAY_LIMIT };

export interface FreePlayStatus {
  isMember: boolean;
  used: number;
  remaining: number;
  unlockedIds: string[];
}

export const freemiumQueries = {
  /** Enabled only when signed in; anon has no plays and never sees the pill. */
  status: (signedIn: boolean) =>
    queryOptions({
      queryKey: ['freemium', 'status'] as const,
      queryFn: async (): Promise<FreePlayStatus> => {
        const { data, error } = await supabase.rpc('free_play_status');
        if (error) throw error;
        const row = data?.[0];
        return {
          isMember: row?.is_member ?? false,
          used: row?.used ?? 0,
          remaining: row?.remaining ?? FREE_PLAY_LIMIT,
          unlockedIds: row?.unlocked_ids ?? [],
        };
      },
      enabled: signedIn,
      staleTime: 1000 * 10,
    }),
};

/**
 * Authoritative server-side play consumption. The player unit (separate) calls
 * this on playback start; exported here as the single client entry to the gate
 * RPC so there is one source of truth.
 */
export async function consumeFreePlay(
  contentId: string,
): Promise<{ allowed: boolean; remaining: number | null }> {
  const { data, error } = await supabase.rpc('consume_free_play', { p_content_id: contentId });
  if (error) throw error;
  const row = data?.[0];
  return { allowed: row?.allowed ?? false, remaining: row?.remaining ?? null };
}
